//MSMEYBPagesList
import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import useFetch from '../../hooks/useFetch';
import 'react-datepicker/dist/react-datepicker.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle, faPlus } from '@fortawesome/free-solid-svg-icons';
import Loader from '../../components/utils/Loader';
import Tooltip from '../../components/utils/Tooltip';
import MainLayout from '../../layouts/MainLayout';

const MSMEYBPagesList = () => {
  const authState = useSelector(state => state.authReducer);
  const { isLoggedIn } = authState;
  const [msmeybPages, setMSMEYBPages] = useState([]);
  const [fetchData, { loading }] = useFetch();
  const [selectedMSMEYBPageId, setSelectedMSMEYBPageId] = useState(null);
  const [deletePageId, setDeletePageId] = useState(null); 
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    document.title = authState.isLoggedIn ? `${authState.user.name}'s dashboard` : "Think5Whys";
  }, [authState]);


  const fetchMSMEYBPages = useCallback(() => {
    const config = {
        url: "/usermsmeybpages/mypagesfull",    //mypagesfull
        method: "post",
        headers: { Authorization: authState.token },
        data: { userId: authState.user._id}
      };
      
    // console.log("Open Text Book Search Payload: ", config);
  
    fetchData(config, { showSuccessToast: false }).then(data => setMSMEYBPages(data.userMSMEYBPages));
  }, [authState.token, fetchData, authState.user._id]);
  
  useEffect(() => {
    if (!authState.isLoggedIn) return;
    fetchMSMEYBPages();
  }, [authState.isLoggedIn, fetchMSMEYBPages]);

  const [isDeleteClicked, setIsDeleteClicked] = useState(false);
  
  const deleteClicked = (id) => {
    setDeletePageId(id);
    setIsDeleteClicked(true);
  }
  
  const noDeleteClicked = () => {
    setIsDeleteClicked(false);
  }

  const handleDelete = (id) => {
    // console.log("ID to delete: ",id);
    const config = {
      url: `/usermsmeybpages/${id}`,
      method: "delete",
      headers: { Authorization: authState.token }
    };
    fetchData(config).then(() => fetchMSMEYBPages());
    setIsDeleteClicked(false);
  }

  const handleView = (msmeybPageId) => {
    setSelectedMSMEYBPageId(prevId => (prevId === msmeybPageId ? null : msmeybPageId));
  };

  const handleMSMEYBPageShare = (msmeybPageId) => {
    setSelectedMSMEYBPageId(msmeybPageId);
  };

  const handleMSMEYBPageSave = (msmeybPageId) => {
    setSelectedMSMEYBPageId(msmeybPageId);
  };

  const handleMSMEYBPagePrint = (msmeybPageId) => {
    setSelectedMSMEYBPageId(msmeybPageId);
  };

  const formatDateTime = (date) => {
    const options = {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
        hour12: false
    };
    const jsDate = new Date(date);
    const day = jsDate.getDate().toString().padStart(2, '0');
    const month = (jsDate.getMonth() + 1).toString().padStart(2, '0');
    const year = jsDate.getFullYear();
    const hours = jsDate.getHours().toString().padStart(2, '0');
    const minutes = jsDate.getMinutes().toString().padStart(2, '0');
    const seconds = jsDate.getSeconds().toString().padStart(2, '0');
    return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
};

const sortedMSMEYBPages = [...msmeybPages].sort((a, b) => b.pageNumber - a.pageNumber);
const pageNumberList = msmeybPages.map(page => page.pageNumber);

const toggleVisibility = () => {
  setIsVisible(!isVisible); // Toggle the visibility state
};


  return (
    <>
      <MainLayout>
        <div className="min-h-full py-8 bg-gradient-to-r from-purple-300 via-blue-500 to-purple-500 mt-12">
          <div className='flex flex-col justify-center items-center'>
            <h2 className='text-xl font-bold text-white text-center mb-2'>MSME Year Book / Journal Pages</h2>
            <h3 className='text-yellow'>You can create your Your Own Year Book page using content sourced from online research, newspapers, television news, or any other reliable source.
              <span className='ml-2 cursor-pointer' onClick={toggleVisibility}>
                <FontAwesomeIcon className="bg-blue-500 mr-4" icon={faInfoCircle} />
              </span>
            </h3>
            
            <h3 className='text-yellow'>You can also explore MSME Bulletins for the latest news and current affairs, curated daily by the Think5Whys team after thorough research.</h3>
          </div>
          {isVisible && (
              <div className="benefits-container mt-4 p-4 bg-gray-800 text-white rounded">
                  <h2 className="text-m font-bold text-white">Advantages of Creating a MSME Year Book Page for Daily noting as Diary or Journal</h2>
                  <section className="benefit-section">
                      <h3 className="text-m font-semibold">1. Comprehensive Knowledge Base</h3>
                      <ul>
                          <li><strong>Diverse Sources:</strong> By sourcing content from various mediums such as online research, newspapers, and television news, MSMEs can gain a well-rounded understanding of current trends. This comprehensive approach ensures they are aware of multiple perspectives on important issues.</li>
                          <li><strong>Up-to-Date Information:</strong> MSME Bulletins provide the latest news updates, keeping MSMEs informed about current events that may be relevant for their business.</li>
                      </ul>
                  </section>
                  <section className="benefit-section">
                      <h3 className="text-m font-semibold">2. Enhanced Critical Thinking</h3>
                      <ul>
                          <li><strong>Analytical Skills:</strong> Curating content from different sources encourages MSMEs to analyze and critically evaluate the information, which is useful for their business.</li>
                          <li><strong>Connection of Ideas:</strong> Researching diverse topics helps MSMEs connect ideas and concepts, fostering a deeper understanding of how different aspects intertwine.</li>
                      </ul>
                  </section>
                  {/* Additional sections go here... */}
                  <h2 className="text-m font-bold text-white">Conclusion</h2>
                  <p>
                      Overall, the approach of creating a MSME Year Book page and utilizing MSME Daily Bulletins provides MSMEs with a multifaceted learning experience, equipping them with the knowledge, skills, and strategies necessary to excel in business and competitive world.
                  </p>
              </div>
            )}
            <div className="border border-gray-300 p-4 mt-4">
              <div className="mt-2">
                  {msmeybPages && msmeybPages.length !== 0 && (
                    <div className="flex justify-between items-center border-b border-b-gray-300">
                      <div>
                      <h3 className='text-m mt-1 mx-1 text-yellow-200'>{msmeybPages.length} Page Found!</h3>
                      </div>
                      <div className='mr-4'>
                      {/* <Link to='/msmeyb/add' className='block w-full h-full px-1 py-2 ml-4 bg-blue-500'>
                        <FontAwesomeIcon className="bg-blue-500 mr-4" icon={faPlus} />  Setup A Page
                      </Link> */}
                      <Link
                          to={`/msmeyb/add`}
                          state={{ pageNumberList: pageNumberList }}
                          className='block w-full h-full px-1 py-2 ml-4 bg-blue-500'
                        >
                          <FontAwesomeIcon className="bg-blue-500 mr-4" icon={faPlus} />  Setup A Page
                      </Link>
                    </div>
                  </div>
                  )}
              </div>
              {loading ? (
                <Loader />
              ) : (
                <div>
                  {msmeybPages && msmeybPages.length === 0 ? (
                    <div className='min-h-[300px] flex items-center justify-center gap-4 text-yellow-300'>
                      <div className="flex justify-between items-center">
                        <h3 className='text-lg mt-1 mx-2 text-orange-400'>Your Own Page Not Found!</h3>
                        <div>
                          {/* <Link to='/msmeyb/add' className='block w-full h-full px-1 py-2 ml-4 bg-blue-500'>
                            <FontAwesomeIcon className="bg-blue-500 mr-4" icon={faPlus} />  Setup A Page
                          </Link> */}
                          <Link
                              to={`/msmeyb/add`}
                              state={{ pageNumberList: pageNumberList }}
                              className='block w-full h-full px-1 py-2 ml-4 bg-blue-500'
                            >
                              <FontAwesomeIcon className="bg-blue-500 mr-4" icon={faPlus} />  Setup A Page
                          </Link>
                        </div>
                    </div>
                  </div>
                  ) : (
                    sortedMSMEYBPages.map((msmeybPage, index) => (
                      <div key={msmeybPage._id} className='bg-gray-600 my-1 p-1 text-white rounded-md shadow-md text-sm'>
                        <div className='flex'>
                          <span className='font-medium'>{msmeybPage.pageNumber}{'. '}{msmeybPage.pageTitle}</span>
                          <Tooltip text={"View details"} position={"top"}>
                            <span className='ml-auto mr-4 text-blue-500 cursor-pointer' onClick={() => handleView(msmeybPage._id)}>
                              <i className="fa-solid fa-eye"></i>
                            </span>
                          </Tooltip>
                          <Tooltip text={"Edit this record"} position={"top"}>
                            <Link
                              to={`/msmeyb/${msmeybPage._id}`}
                              state={{ pageNumberList: pageNumberList }}
                              className='mr-4 text-green-600 cursor-pointer'
                            >
                              <i className="fa-solid fa-pen"></i>
                            </Link>
                          </Tooltip>
                          <Tooltip text={"Delete this record"} position={"top"}>
                            <span className='text-red-500 cursor-pointer mr-2' onClick={() => deleteClicked(msmeybPage._id)}>
                              <i className="fa-solid fa-trash"></i>
                            </span>
                          </Tooltip>
                          { isDeleteClicked && (
                            <div
                              className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center"
                              style={{ zIndex: 1000 }}
                            >
                              <div className="w-6/12 flex flex-row justify-center bg-blue-200 text-black border-b border-b-gray-300 mr-4 ml-4 p-4">
                                  <button
                                    className="bg-red-700 w-40"
                                    onClick={() =>  handleDelete(deletePageId) }
                                    style={{ maxWidth: "150px" }}
                                  >
                                    Confirm To Delete
                                  </button>
                                  <button
                                    className="bg-yellow-300 ml-4 text-black w-40"
                                    onClick={() =>  noDeleteClicked() }
                                    style={{ maxWidth: "150px" }}
                                  >
                                    No I Do Not Want To Delete
                                  </button>
                              </div>
                            </div>
                          )}
                        </div>
                        {selectedMSMEYBPageId === msmeybPage._id && (
                          <div className='bg-gray-600 text-white'>
                            <p className='bg-blue-700 text-white justify-center text-m mt-2 mb-2'>
                              <h2 className='text-white'><strong>Page Details</strong></h2>
                            </p>
                            <p><strong>Page#:</strong> {msmeybPage.pageNumber}</p>
                            <p><strong>Date:</strong> {formatDateTime(msmeybPage.msmeybDate)}</p>

                            <p><strong>Page Title:</strong> {msmeybPage.pageTitle}</p>
                            <p className='border-2 border-lime-600 px-4 py-4'><strong className='text-yellow-200'>Page Content:</strong>  <br/>
                              <div
                                className="ml-2 mr-2 flex items-start"
                                style={{ minHeight: "100%", position: 'relative', top: 0, left: 0, right: 0 }}
                                dangerouslySetInnerHTML={{ __html: msmeybPage.pageContent }}
                              />
                            </p>
                            <p><strong>Is Hidden Text:</strong> {msmeybPage.isSplText === 1 ? "Yes":"No"}</p>
                            <p className='border-2 border-lime-600 px-4 py-4'><strong className='text-yellow-200'>Hidden Text:</strong><br/>
                              {msmeybPage.splTextContent}
                            </p>
                            <p><strong>Speak Hidden Text:</strong> {msmeybPage.readFromSplText ? "Yes":"No"}</p>
                            <p><strong>Show Page Video:</strong> {msmeybPage.hasVideo ? "Yes":"No"}</p>
                            <p><strong>Video URL:</strong> {msmeybPage.videoURL}</p>
                            <p><strong>Page Keywords:</strong> {msmeybPage.searchTags}</p>
                            <p><strong>Date Created:</strong> {formatDateTime(msmeybPage.createdDate)}</p>
                            <p><strong>Creator Name:</strong> {msmeybPage.createdByName}</p>

                            <Tooltip text={"Share"} position={"top"}>
                              <span className='text-green-500 cursor-pointer mr-4' disabled={true} onClick={() => handleMSMEYBPageShare(msmeybPage._id)}>
                                <i className="fa-solid fa-share"></i>
                              </span>
                            </Tooltip>
                            <Tooltip text={"Save"} position={"top"}>
                              <span className='text-blue-500 cursor-pointer mr-4' disabled={true} onClick={() => handleMSMEYBPageSave(msmeybPage._id)}>
                                <i className="fa-solid fa-save"></i>
                              </span>
                            </Tooltip>
                            <Tooltip text={"Print"} position={"top"}>
                              <span className='text-orange-500 cursor-pointer mr-4' disabled={true} onClick={() => handleMSMEYBPagePrint(msmeybPage._id)}>
                                <i className="fa-solid fa-print"></i>
                              </span>
                            </Tooltip>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
      </MainLayout>
    </>
  )
}

export default MSMEYBPagesList